const OPENWEATHER_API_KEY = process.env.NEXT_PUBLIC_OPENWEATHER_API_KEY;
const BASE_URL = 'https://api.openweathermap.org/data/2.5';

/**
 * Get current weather for a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} Current weather data
 */
export async function getCurrentWeather(lat, lon) {
  if (!OPENWEATHER_API_KEY) {
    throw new Error('OpenWeather API key not configured');
  }

  const response = await fetch(
    `${BASE_URL}/weather?lat=${lat}&lon=${lon}&appid=${OPENWEATHER_API_KEY}&units=imperial`
  );
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('401: Invalid OpenWeather API key. Please check your API key configuration.');
    } else if (response.status === 429) {
      throw new Error('429: OpenWeather API rate limit exceeded. Please try again later.');
    } else {
      throw new Error(`Weather API error: ${response.status} - ${response.statusText}`);
    }
  }
  
  return response.json();
}

/**
 * Get 5-day weather forecast for a location
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<Object>} Forecast data
 */
export async function getWeatherForecast(lat, lon) {
  if (!OPENWEATHER_API_KEY) {
    throw new Error('OpenWeather API key not configured');
  }

  const response = await fetch(
    `${BASE_URL}/forecast?lat=${lat}&lon=${lon}&appid=${OPENWEATHER_API_KEY}&units=imperial`
  );
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('401: Invalid OpenWeather API key. Please check your API key configuration.');
    } else if (response.status === 429) {
      throw new Error('429: OpenWeather API rate limit exceeded. Please try again later.');
    } else {
      throw new Error(`Weather API error: ${response.status} - ${response.statusText}`);
    }
  }
  
  return response.json();
}

/**
 * Get weather data for a specific date from forecast
 * @param {Object} forecastData - Forecast API response
 * @param {Date} targetDate - Date to get weather for
 * @returns {Object|null} Weather data for specific date
 */
export function getWeatherForDate(forecastData, targetDate) {
  if (!forecastData?.list) return null;
  
  const targetDateStr = targetDate.toDateString();
  
  // Find the forecast entry closest to the target date
  const closestForecast = forecastData.list.find(item => {
    const itemDate = new Date(item.dt * 1000);
    return itemDate.toDateString() === targetDateStr;
  });
  
  return closestForecast || null;
}

/**
 * Get clothing recommendations based on weather conditions
 * @param {number} temp - Temperature in Fahrenheit
 * @param {string} conditions - Weather conditions
 * @returns {Array<string>} Array of clothing recommendations
 */
export function getClothingRecommendations(temp, conditions) {
  const recommendations = [];
  
  // Temperature-based recommendations
  if (temp < 32) {
    recommendations.push('Heavy winter coat', 'Warm hat and gloves', 'Insulated boots');
  } else if (temp < 50) {
    recommendations.push('Winter coat or heavy jacket', 'Warm hat', 'Warm socks');
  } else if (temp < 65) {
    recommendations.push('Light jacket or sweater', 'Long pants', 'Comfortable shoes');
  } else if (temp < 80) {
    recommendations.push('Light clothing', 'Comfortable shoes', 'Sunscreen');
  } else {
    recommendations.push('Light, breathable clothing', 'Hat for sun protection', 'Sunscreen');
  }
  
  // Condition-based recommendations
  if (conditions?.toLowerCase().includes('rain')) {
    recommendations.push('Waterproof jacket', 'Umbrella', 'Waterproof shoes');
  } else if (conditions?.toLowerCase().includes('snow')) {
    recommendations.push('Waterproof boots', 'Snow pants', 'Waterproof gloves');
  } else if (conditions?.toLowerCase().includes('wind')) {
    recommendations.push('Windproof jacket', 'Secure hat');
  }
  
  return recommendations;
}

/**
 * Format temperature with proper units
 * @param {number} temp - Temperature in Fahrenheit
 * @returns {string} Formatted temperature
 */
export function formatTemperature(temp) {
  if (temp === undefined || temp === null) return 'N/A';
  return `${Math.round(temp)}°F`;
}

/**
 * Get weather icon based on conditions
 * @param {string} iconCode - OpenWeather icon code
 * @returns {string} Heroicon name
 */
export function getWeatherIcon(iconCode) {
  const iconMap = {
    '01d': 'SunIcon',
    '01n': 'MoonIcon',
    '02d': 'CloudIcon',
    '02n': 'CloudIcon',
    '03d': 'CloudIcon',
    '03n': 'CloudIcon',
    '04d': 'CloudIcon',
    '04n': 'CloudIcon',
    '09d': 'CloudIcon',
    '09n': 'CloudIcon',
    '10d': 'CloudIcon',
    '10n': 'CloudIcon',
    '11d': 'BoltIcon',
    '11n': 'BoltIcon',
    '13d': 'CloudIcon',
    '13n': 'CloudIcon',
    '50d': 'CloudIcon',
    '50n': 'CloudIcon'
  };
  
  return iconMap[iconCode] || 'CloudIcon';
}
